import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 403 &&
      error.response?.data?.error === "MODULE_MISMATCH"
    ) {
      // Redirect to the correct module based on what the server rejected
      const url = error.config?.url || "";
      if (url.includes("/manufacturing")) {
        window.location.href = "/dashboard";
      } else {
        window.location.href = "/manufacturing/dashboard";
      }
    }
    return Promise.reject(error);
  },
);

// Projects API
export const projectsAPI = {
  getAll: (params = {}) => api.get("/projects", { params }),
  getMyActionProjects: (params = {}) =>
    api.get("/projects/my-action-projects", { params }),
  getFolders: () => api.get("/projects/folders"),

  getOverview: () => api.get("/projects/overview"),
  workflowOptions: () => api.get("/projects/workflow-options"),

  getById: (id) => api.get(`/projects/${id}`),
  getPortfolioGantt: () => api.get("/projects/gantt"),
  getHistory: (id) => api.get(`/projects/${id}/history`),

  create: (data) => api.post("/projects", data),

  update: (id, data) => api.put(`/projects/${id}`, data),
  setUrgency: (id, isUrgent) =>
    api.patch(`/projects/${id}/urgency`, { isUrgent }),

  delete: (id) => api.delete(`/projects/${id}`),

  getArchived: (params = {}) => api.get("/projects/archived", { params }),
  unarchive: (id) => api.patch(`/projects/${id}/unarchive`),

  updateStatus: (id, status) => api.patch(`/projects/${id}/status`, { status }),
  forceStatus: (id, status, reason) =>
    api.patch(`/projects/${id}/force-status`, { status, reason }),
  flagForInvoicing: (id) => api.patch(`/projects/${id}/flag-for-invoicing`),
  getInvoicingQueue: (params = {}) =>
    api.get("/projects/invoicing-queue", { params }),
  downloadInvoicingReport: (params = {}) =>
    api.get("/projects/invoicing-report", { params, responseType: "blob" }),
  projectFileUrl: (filePath, { download = false } = {}) =>
    `${process.env.REACT_APP_API_UPLOAD_URL}${filePath}?token=${encodeURIComponent(
      localStorage.getItem("accessToken") || "",
    )}${download ? "&download=1" : ""}`,

  invoiceFileUrl: (filePath) =>
    `${process.env.REACT_APP_API_UPLOAD_URL}${filePath}?token=${encodeURIComponent(
      localStorage.getItem("accessToken") || "",
    )}`,

  markInvoiced: (id, { invoiceNumber, invoiceFile } = {}) => {
    if (invoiceFile) {
      const formData = new FormData();
      formData.append("invoiceFile", invoiceFile);
      return api.patch(`/projects/${id}/mark-invoiced`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
    return api.patch(
      `/projects/${id}/mark-invoiced`,
      invoiceNumber ? { invoiceNumber } : {},
    );
  },

  allocateUser: (id, userId) =>
    api.post(`/projects/${id}/allocations`, { userId }),
  allocationEligibleUsers: () => api.get("/projects/allocation-eligible-users"),

  removeAllocation: (id, userId) =>
    api.delete(`/projects/${id}/allocations/${userId}`),

  createFolder: (payload) => api.post("/projects/folders", payload),
  updateFolder: (folderId, payload) =>
    api.patch(`/projects/folders/${folderId}`, payload),
  deleteFolder: (folderId) => api.delete(`/projects/folders/${folderId}`),
  moveProjectsToFolder: (projectIds, folderId = null) =>
    api.patch("/projects/folders/move-projects", { projectIds, folderId }),
  removeProjectsFromFolder: (folderId, projectIds) =>
    api.patch(`/projects/folders/${folderId}/remove-projects`, { projectIds }),

  // stageNames / userIds narrow the report to a selection; omit both for the
  // full project report.
  downloadReport: (id, { stageNames = [], userIds = [] } = {}) => {
    const params = new URLSearchParams();
    stageNames.forEach((name) => params.append("stages", name));
    userIds.forEach((userId) => params.append("users", userId));
    const query = params.toString();
    return api.get(`/projects/${id}/report${query ? `?${query}` : ""}`, {
      responseType: "blob",
    });
  },
};

export const customersAPI = {
  getAll: () => api.get("/customers"),
  create: (name) => api.post("/customers", { name }),
};

/*
  The customer portal is public and must never borrow a staff session: no
  Authorization header, no cookies, and none of the interceptors above — a
  customer hitting an expired link should see the portal's own message, not get
  bounced to a staff dashboard.
*/
const portalClient = axios.create({ baseURL: API_URL, withCredentials: false });

export const portalPublicAPI = {
  get: (token) => portalClient.get(`/portal/${encodeURIComponent(token)}`),
};

export const portalLinksAPI = {
  getAll: () => api.get("/manufacturing/portal-links"),
  create: (payload) => api.post("/manufacturing/portal-links", payload),
  insights: (id) => api.get(`/manufacturing/portal-links/${id}/insights`),
  rotate: (id) => api.post(`/manufacturing/portal-links/${id}/rotate`),
  revoke: (id) => api.patch(`/manufacturing/portal-links/${id}/revoke`),
  extend: (id, validityDays) =>
    api.patch(`/manufacturing/portal-links/${id}/extend`, { validityDays }),
};

export const poLocationsAPI = {
  getAll: () => api.get("/manufacturing/po-locations"),
  create: (name) => api.post("/manufacturing/po-locations", { name }),
};

export const usersAPI = {
  getActivityReport: (userId, from, to) =>
    api.get(`/users/${userId}/activity-report`, { params: { from, to } }),
  downloadActivityReport: (userId, from, to, stageNames, fields) =>
    api.get(`/users/${userId}/activity-report/pdf`, {
      params: {
        from,
        to,
        ...(stageNames?.length ? { stageName: stageNames.join(",") } : {}),
        ...(fields?.length ? { fields: fields.join(",") } : {}),
      },
      responseType: "blob",
    }),
  downloadActivityReportsZip: (userIds, from, to) =>
    api.get("/users/activity-reports/zip", {
      params: { userIds: userIds.join(","), from, to },
      responseType: "blob",
      timeout: 0,
    }),
  getTeamAttendanceReport: (userIds, from, to) =>
    api.get("/users/team/attendance", {
      params: { userIds: userIds.join(","), from, to },
    }),
  downloadTeamAttendanceReport: (userIds, from, to) =>
    api.get("/users/team/attendance/pdf", {
      params: { userIds: userIds.join(","), from, to },
      responseType: "blob",
    }),
};

export const stageAssignmentsAPI = {
  create: (projectId, payload) =>
    api.post(`/projects/${projectId}/stage-assignments`, payload),
  createBatch: (projectId, assignments) =>
    api.post(`/projects/${projectId}/stage-assignments/batch`, { assignments }),
  reassign: (projectId, assignmentId, payload) =>
    api.patch(
      `/projects/${projectId}/stage-assignments/${assignmentId}`,
      payload,
    ),
  reschedule: (projectId, assignmentId, payload) =>
    api.patch(
      `/projects/${projectId}/stage-assignments/${assignmentId}/reschedule`,
      payload,
    ),
  remove: (projectId, assignmentId) =>
    api.post(`/projects/${projectId}/stage-assignments/${assignmentId}/remove`),
  eligibleUsers: (projectId, stageId) =>
    api.get(
      `/projects/${projectId}/stage-assignments/${stageId}/eligible-users`,
    ),
  eligibleUsersForReassign: (projectId, stageId, excludeUserId) =>
    api.get(
      `/projects/${projectId}/stage-assignments/${stageId}/reassign-eligible-users`,
      {
        params: excludeUserId ? { excludeUserId } : {},
      },
    ),
  submitTasks: (projectId, assignmentId, formData) =>
    api.post(
      `/projects/${projectId}/stage-assignments/${assignmentId}/submissions`,
      formData,
    ),
  review: (projectId, assignmentId, payload) =>
    api.post(
      `/projects/${projectId}/stage-assignments/${assignmentId}/review`,
      payload,
    ),
  getStageHistory: (projectId, stageId) =>
    api.get(`/projects/${projectId}/stage-assignments/${stageId}/history`),
  getStageNotes: (projectId, stageId) =>
    api.get(`/projects/${projectId}/stage-assignments/${stageId}/notes`),
  addStageNote: (projectId, assignmentId, body) =>
    api.post(`/projects/${projectId}/stage-assignments/${assignmentId}/notes`, {
      body,
    }),
  // instructions: [{ assignmentId, body }] — one note per assignee, one save.
  addStageInstructions: (projectId, stageId, instructions) =>
    api.post(`/projects/${projectId}/stages/${stageId}/instructions`, {
      instructions,
    }),
  checkConflicts: (projectId, payload) =>
    api.post(
      `/projects/${projectId}/stage-assignments/check-conflicts`,
      payload,
    ),
};

export const projectDocumentsAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/documents`),

  downloadUrl: (projectId, docType) =>
    `${API_URL}/projects/${projectId}/documents/download?token=${encodeURIComponent(
      localStorage.getItem("accessToken") || "",
    )}`,

  previewUrl: (projectId) =>
    `${API_URL}/projects/${projectId}/documents/view?token=${encodeURIComponent(
      localStorage.getItem("accessToken") || "",
    )}`,
};

// Upload API
export const uploadAPI = {
  uploadMaster: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/upload/master", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  uploadDetailsA: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/upload/details-a", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  uploadDetailsB: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/upload/details-b", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  bulkUpload: (masterPath, detailsAPath, detailsBPath) =>
    api.post("/upload/bulk", {
      masterFilePath: masterPath,
      detailsAFolder: detailsAPath,
      detailsBFolder: detailsBPath,
    }),

  bulkUploadFiles: (masterFile, detailsAFiles = [], detailsBFiles = []) => {
    const formData = new FormData();
    if (masterFile) formData.append("master", masterFile);
    detailsAFiles.forEach((file) => formData.append("detailsA", file));
    detailsBFiles.forEach((file) => formData.append("detailsB", file));
    return api.post("/upload/bulk-files", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// Export API
export const exportAPI = {
  json: (fields = null, projectId = null) => {
    let url = "/export/json";
    const params = [];
    if (fields && fields.length > 0) params.push(`fields=${fields.join(",")}`);
    if (projectId) params.push(`project_id=${projectId}`);
    if (params.length > 0) url += "?" + params.join("&");
    return api.get(url);
  },

  csv: (fields = null, projectId = null) => {
    let url = "/export/csv";
    const params = [];
    if (fields && fields.length > 0) params.push(`fields=${fields.join(",")}`);
    if (projectId) params.push(`project_id=${projectId}`);
    if (params.length > 0) url += "?" + params.join("&");
    return api.get(url, { responseType: "blob" });
  },

  getColumns: () => api.get("/export/columns"),
};

export const subscriptionAPI = {
  getStatus: () => api.get("/tenant/subscription"),
  getPricing: () => api.get("/tenant/subscription/pricing"),
  createOrder: (totalUsers, durationMonths) =>
    api.post("/tenant/subscription/order", { totalUsers, durationMonths }),
  extend: (payload) => api.post("/tenant/subscription/extend", payload),
  getHistory: () => api.get("/tenant/subscription/history"),
  downloadInvoice: (paymentId) =>
    api.get(`/tenant/subscription/invoice/${paymentId}`, {
      responseType: "blob",
    }),
};

export const tenantAPI = {
  customizeTenantImage: (file) => {
    const formData = new FormData();
    formData.append("file", file);

    return api.patch("/tenant/customize", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  customizeTenantData: (minimumWarning, minimumOverdue, projectFormSchema) => {
    const formData = new FormData();
    formData.append("minimumWarning", minimumWarning || "0");
    formData.append("minimumOverdue", minimumOverdue || "0");
    if (projectFormSchema !== undefined) {
      formData.append("projectFormSchema", JSON.stringify(projectFormSchema));
    }

    return api.patch("/tenant/customize", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  customizeProjectForm: (projectFormSchema) => {
    const formData = new FormData();
    formData.append("projectFormSchema", JSON.stringify(projectFormSchema));
    return api.patch("/tenant/customize", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  saveStatusColors: (statusColorConfig) => {
    const formData = new FormData();
    formData.append("statusColorConfig", JSON.stringify(statusColorConfig));
    return api.patch("/tenant/customize", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  getTenant: () => api.get("/tenant"),
};

export const scheduleAPI = {
  getOrg: () => api.get("/schedule/org"),
  updateOrg: (payload) => api.put("/schedule/org", payload),
  addHoliday: (payload) => api.post("/schedule/org/holidays", payload),
  removeHoliday: (holidayId) =>
    api.delete(`/schedule/org/holidays/${holidayId}`),
  getUser: (userId) => api.get(`/schedule/users/${userId}`),
  createUser: (userId, payload) =>
    api.post(`/schedule/users/${userId}`, payload),
  calculateEndDate: (userId, params) =>
    api.get(`/schedule/users/${userId}/calculate-end-date`, { params }),
  suggestStartTime: (userId, params) =>
    api.get(`/schedule/users/${userId}/suggest-start-time`, { params }),
};

export const queueAPI = {
  getUserQueue: (userId, params) => api.get(`/queue/${userId}`, { params }),
  reorderQueue: (userId, orderedAssignmentIds) =>
    api.put(`/queue/${userId}/reorder`, { orderedAssignmentIds }),
};

export const plannerAPI = {
  getTeam: (params) => api.get("/planner/team", { params }),
  getUser: (userId, params) => api.get(`/planner/user/${userId}`, { params }),
  updateUserSchedule: (userId, payload) =>
    api.put(`/planner/user/${userId}/schedule`, payload),
  reorder: (payload) => api.put("/planner/reorder", payload),
  getUserDayOverrides: (userId, params) =>
    api.get(`/planner/user/${userId}/day-overrides`, { params }),
  upsertDayOverride: (userId, payload) =>
    api.post(`/planner/user/${userId}/day-override`, payload),
  removeDayOverride: (userId, dateStr) =>
    api.delete(`/planner/user/${userId}/day-override/${dateStr}`),
  exportTeam: (params) =>
    api.get("/planner/export", { params, responseType: "blob" }),
  getArchives: () => api.get("/planner/archives"),
};

export const timeLogsAPI = {
  start: (stageAssignmentId) =>
    api.post("/time-logs/start", { stageAssignmentId }),
  pause: (stageAssignmentId) =>
    api.post("/time-logs/pause", { stageAssignmentId }),
  resume: (stageAssignmentId) =>
    api.post("/time-logs/resume", { stageAssignmentId }),
  active: () => api.get("/time-logs/active"),
  additionalWorkState: (projectId) =>
    api.get(`/time-logs/additional/project/${projectId}`),
  startAdditionalWork: (projectId, reason) =>
    api.post("/time-logs/additional/start", { projectId, reason }),
  userLogs: (userId, params) =>
    api.get(`/time-logs/user/${userId}`, { params }),
  projectSummary: (projectId) =>
    api.get(`/time-logs/project/${projectId}/summary`),
  projectReport: (projectId) =>
    api.get(`/time-logs/project/${projectId}/report`, { responseType: "blob" }),
  edit: (timeLogId, payload) => api.patch(`/time-logs/${timeLogId}`, payload),
};

export const teamActivityAPI = {
  list: (params) => api.get("/team/activity", { params }),
};

export default api;

export const tasksAPI = {
  list: (params = {}) => api.get("/tasks", { params }),
  get: (taskId) => api.get(`/tasks/${taskId}`),
  create: (data) => api.post("/tasks", data),
  update: (taskId, data) => api.patch(`/tasks/${taskId}`, data),
  remove: (taskId) => api.delete(`/tasks/${taskId}`),
  addProject: (taskId, projectId) =>
    api.post(`/tasks/${taskId}/projects`, { projectId }),
  removeProject: (taskId, projectId) =>
    api.delete(`/tasks/${taskId}/projects/${projectId}`),
  addDependency: (taskId, dependsOnId) =>
    api.post(`/tasks/${taskId}/dependencies`, { dependsOnId }),
  removeDependency: (taskId, dependsOnId) =>
    api.delete(`/tasks/${taskId}/dependencies/${dependsOnId}`),
  addComment: (taskId, body) => api.post(`/tasks/${taskId}/comments`, { body }),
  logTime: (taskId, data) => api.post(`/tasks/${taskId}/time-logs`, data),
  decide: (taskId, decision) =>
    api.post(`/tasks/${taskId}/decision`, { decision }),
  moveToSection: (taskId, projectId, sectionId) =>
    api.put(`/tasks/${taskId}/projects/${projectId}/section`, { sectionId }),
  projectSections: (projectId) => api.get(`/tasks/projects/${projectId}/sections`),
  createProjectSection: (projectId, name) =>
    api.post(`/tasks/projects/${projectId}/sections`, { name }),
  reorderProjectSections: (projectId, sectionIds) =>
    api.put(`/tasks/projects/${projectId}/sections/order`, { sectionIds }),
  renameProjectSection: (sectionId, name) =>
    api.patch(`/tasks/sections/${sectionId}`, { name }),
  deleteProjectSection: (sectionId) => api.delete(`/tasks/sections/${sectionId}`),
  removeTimeLog: (taskId, logId) =>
    api.delete(`/tasks/${taskId}/time-logs/${logId}`),
  removeComment: (taskId, commentId) =>
    api.delete(`/tasks/${taskId}/comments/${commentId}`),
  addAttachment: (taskId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/tasks/${taskId}/attachments`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  removeAttachment: (taskId, attachmentId) =>
    api.delete(`/tasks/${taskId}/attachments/${attachmentId}`),
  attachmentUrl: (filePath, { download = false } = {}) =>
    `${process.env.REACT_APP_API_UPLOAD_URL || ""}${filePath}?token=${encodeURIComponent(
      localStorage.getItem("accessToken") || "",
    )}${download ? "&download=1" : ""}`,
  dashboard: () => api.get("/tasks/dashboard"),
  teamOverview: () => api.get("/tasks/team-overview"),
  getSections: () => api.get("/tasks/sections"),
  saveSections: (sections) => api.put("/tasks/sections", { sections }),
  people: () => api.get("/tasks/people"),
  projectOptions: () => api.get("/tasks/project-options"),
  search: (q, excludeId) =>
    api.get("/tasks/search", { params: { q, excludeId } }),
};

export const inboxAPI = {
  list: (filter = "all") => api.get("/inbox", { params: { filter } }),
  unreadCount: () => api.get("/inbox/unread-count"),
  markRead: (id) => api.post(`/inbox/${id}/read`),
  markUnread: (id) => api.post(`/inbox/${id}/unread`),
  markAllRead: () => api.post("/inbox/read-all"),
};
